import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { PageMeta, PageHero, Section, SectionHeading, FeatureCard, CtaBlock, Pill } from "@/components/PageShellParts";
import { Activity, AlertTriangle, ClipboardCheck, ClipboardList, Clock, FileSearch, FileText, Layers, Sparkles, UserCheck, Workflow } from "lucide-react";

const Icons = { Activity, AlertTriangle, ClipboardCheck, ClipboardList, Clock, FileSearch, FileText, Layers, Sparkles, UserCheck, Workflow };

export default function ForClinicsPage() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <PageMeta title="For Clinics" description="AI-assisted assessment workflow support for clinics and mental health professionals." />
      <div className="pt-20">
      <PageHero
        eyebrow="For Clinics"
        title="AI-Assisted Assessment Support for Clinics"
        subtitle="PsyTech helps clinics and mental health professionals structure intake, assessment, and reporting workflows — without replacing clinical judgement."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="cyan">Structured intake</Pill>
          <Pill tone="cyan">Professional review</Pill>
          <Pill tone="cyan">Documentation support</Pill>
        </div>
      </PageHero>

      <Section>
        <SectionHeading
          eyebrow="Pain points we hear"
          title="Where clinical teams lose time today"
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.Clock} title="Long intake processes">Unstructured intake can consume valuable session time before clinical work begins.</FeatureCard>
          <FeatureCard icon={Icons.Layers} title="Fragmented information">Responses, notes, and prior context scattered across documents and systems.</FeatureCard>
          <FeatureCard icon={Icons.Workflow} title="Manual scoring and summarization">Repetitive effort on work that can be structured and organized.</FeatureCard>
          <FeatureCard icon={Icons.FileText} title="Inconsistent documentation">Hand-written summaries vary between professionals and over time.</FeatureCard>
          <FeatureCard icon={Icons.Activity} title="High administrative load">Paperwork and follow-ups compete with time spent caring for patients.</FeatureCard>
          <FeatureCard icon={Icons.UserCheck} title="Hard to prepare follow-ups">Preparing for a next session takes effort that could be spent in the session itself.</FeatureCard>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading
          eyebrow="How PsyTech helps"
          title="Structured support that stays under professional review"
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.ClipboardList} title="Structured digital assessment flow">Consistent, guided assessments that users can complete with a clear experience.</FeatureCard>
          <FeatureCard icon={Icons.Layers} title="Organized user responses">All responses grouped into a professional-review-friendly format.</FeatureCard>
          <FeatureCard icon={Icons.Sparkles} title="AI-assisted summaries">Structured summaries highlighting consistency, patterns, and open areas for further review.</FeatureCard>
          <FeatureCard icon={Icons.FileSearch} title="Report-style outputs">Outputs designed to be inspected and edited by the reviewing professional.</FeatureCard>
          <FeatureCard icon={Icons.UserCheck} title="Professional review support">Workflows built around professional sign-off rather than bypassing it.</FeatureCard>
          <FeatureCard icon={Icons.ClipboardCheck} title="Clearer follow-up discussion points">Faster preparation for subsequent sessions with structured starting points.</FeatureCard>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Boundaries" title="What PsyTech does not do" />
        <div className="grid md:grid-cols-2 gap-3">
          {[
            "No diagnosis",
            "No treatment plan",
            "No medication advice",
            "No replacement for a licensed clinician",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700">{t}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Use cases" title="Where clinics start with PsyTech" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.ClipboardList} title="Intake support">Structured, user-friendly intake before the first clinical conversation.</FeatureCard>
          <FeatureCard icon={Icons.FileSearch} title="Pre-consultation summary">A quick-to-review summary for the clinician before the session.</FeatureCard>
          <FeatureCard icon={Icons.FileText} title="Assessment documentation">Consistent, structured documentation generated alongside the workflow.</FeatureCard>
          <FeatureCard icon={Icons.Activity} title="Follow-up preparation">Faster preparation of next-step discussions and action points.</FeatureCard>
          <FeatureCard icon={Icons.Sparkles} title="Pilot and research workflows">Structured formats that make evaluation and iteration easier.</FeatureCard>
          <FeatureCard icon={Icons.UserCheck} title="Team training & onboarding">Consistent assessment experience across a growing team.</FeatureCard>
        </div>
      </Section>

      <CtaBlock
        title="Explore a clinic pilot"
        subtitle="We will walk through how PsyTech would fit into your specific intake and review workflow, and agree together on the safeguards required."
        primaryHref="/pilot-program"
        primaryLabel="Explore the pilot program"
        secondaryHref="/#contact"
        secondaryLabel="Contact PsyTech"
        testid="for-clinics-cta"
      />
    </div>
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
            It does not provide diagnosis, treatment, medication advice, or emergency support.
            AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
